﻿/* global React, Icon, Card, CardHead, Button, IconButton, Switch, Select, Segmented, Tabs, Badge, EmptyState, toast, SectionTitle, fmt, fmt0 */
// ============================================================
// PLUMME 2.0 - Innsendinger (MVA) + Eiendeler
// ============================================================
// ============================================================
// MVA - Terminer / Oversikt / termin-detalj
// ============================================================
const readVatFilingModel = (year) => window.PlummeBackend?.getVatFilingModel?.({ year }) || { year, terms: [], totals: { toPay: 0, paid: 0, receivable: 0, refunded: 0 }, vatRegistered: false };
const parseAmount = (value) => window.PlummeMoneyInput.number(value, 0);
const TERM_STATUS = {
  'ikke-levert': { get label() { return t('vat.filing.notFiled', 'Ikke levert'); }, cls: 'pp-warn dot' },
  'kommende': { get label() { return t('vat.filing.upcomingStatus', 'Kommende'); }, cls: 'pp-muted dot' },
  'arkiv': { get label() { return t('vat.filing.importedArchive', 'Importert arkiv'); }, cls: 'pp-muted dot' },
  'levert': { get label() { return t('vat.filing.statusFiled'); }, cls: 'pp-muted dot' },
  'levert-betalt': { get label() { return t('vat.filing.statusFiledPaid'); }, cls: 'pp-muted dot dot-green' },
};
const amountMeta = (term, formatAmount = fmt) => {
  if (term.kind === 'tilgode') return { label: t('vat.vatReceivable', 'MVA til gode'), value: '+' + formatAmount(term.amount), color: 'var(--success)' };
  if (term.kind === 'betalt') return { label: t('voucherList.paid', 'Betalt'), value: formatAmount(term.amount), color: 'var(--ink)' };
  if (term.kind === 'abetale') return { label: t('vat.vatToPay', 'MVA å betale'), value: formatAmount(term.amount), color: 'var(--ink)' };
  return { label: t('vat.noVat', 'Ingen MVA'), value: '0 kr', color: 'var(--muted)' };
};
const groupVatTerms = (terms = []) => ({
  needsAction: terms.filter((term) => term.status === 'ikke-levert'),
  upcoming: terms.filter((term) => term.status === 'kommende'),
  importedArchive: terms.filter((term) => term.status === 'arkiv'),
  earlier: terms.filter((term) => term.status === 'levert' || term.status === 'levert-betalt'),
});
const vatOverviewTermMeta = (term, formatAmount = fmt) => {
  return {
    n: term.n,
    year: term.year,
    period: term.period,
    label: term.kind === 'tilgode' ? t('vat.receivableShort', 'Til gode') : term.kind === 'betalt' ? t('voucherList.paid', 'Betalt') : term.kind === 'abetale' ? t('vat.remainingToPay', 'Gjenstår å betale') : t('vat.noVat', 'Ingen MVA'),
    value: ['tilgode', 'betalt', 'abetale'].includes(term.kind) ? formatAmount(term.amount) : '0 kr',
  };
};
const vatInitialWorkflowState = (term = {}) =>
  term.status === 'levert' || term.status === 'levert-betalt' ? 'levert' : 'opprett';
// Plumme cannot file the return; the user files with Skatteetaten and marks the term (vat-filing.jsx).
const vatMainActionLabel = (workflowState) => window.vatFilingActionLabel(workflowState, false);
const vatDirectActionLabel = (workflowState) => window.vatFilingActionLabel(workflowState, true);

function TerminBadge({ n, active }) {
  return <div className="mva-termbadge" style={active ? { background: 'var(--plum-700)', color: '#fff' } : null} data-help-title={t('vat.term')} data-help={t('vat.termHelp')}>
    <span className="mva-termbadge-n">{n}</span><span className="mva-termbadge-l">{t('vat.termShort')}</span>
  </div>;
}

function TerminCard({ term, onOpen }) {
  const st = TERM_STATUS[term.status];const am = amountMeta(term);
  const action = term.status === 'ikke-levert';
  return <button className={'mva-termcard' + (action ? ' action' : '')} onClick={() => onOpen(term)}>
    <TerminBadge n={term.n} active={action} />
    <div className="mva-termcard-main">
      <div className="t-h3" style={{ fontSize: 16 }}>{t('vat.termLabel', '{n}. termin {year}', { n: term.n, year: term.year })}</div>
      <div className="t-sm muted" style={{ fontWeight: 600 }}>{term.period}</div>
      {action && <div className="mva-termcard-deadline" style={{ marginTop: 8 }}><span className={'pp ' + st.cls}>{st.label}</span><span className="t-sm muted" style={{ marginLeft: 10, fontWeight: 600 }}>{t('sharedText.vat.due')} {term.frist}</span></div>}
    </div>
    {!action && <div className="mva-termcard-status"><span className={'pp ' + st.cls}>{st.label}</span><div className="t-xs muted" style={{ marginTop: 8, fontWeight: 700, letterSpacing: '.04em' }}>{t('vat.deadline')}</div><div className="t-sm" style={{ fontWeight: 800 }}>{term.frist}</div></div>}
    <div className="mva-termcard-amount" data-help-title={am.label} data-help={t('vat.amountHelp')}>
      <div className="t-xs muted" style={{ fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>{am.label}</div>
      <div className="t-h2 t-num" style={{ color: am.color, marginTop: 4 }}>{am.value}</div>
      {action && <div className="mva-termcard-cta" style={{ marginTop: 10 }}><span className="btn btn-primary btn-sm">{t('vat.openReturn')}<Icon name="chevRight" size={15} /></span></div>}
    </div>
  </button>;
}

function VatTermsView({ terms, onOpen }) {
  const grouped = groupVatTerms(terms);
  const Section = ({ label, items }) => items.length === 0 ? null : <div style={{ marginBottom: 26 }}>
    <div className="mva-section-label" data-help-title={label} data-help={t('vat.sortHelp')}>{label}<span className="mva-count">{items.length}</span></div>
    <div className="col gap-12">{items.map((term) => <TerminCard key={term.key} term={term} onOpen={onOpen} />)}</div>
  </div>;
  if (!terms.length) return <EmptyState acc="none" mood="happy" title={t('vat.noTerms')} text={t('vat.noTermsHelp', 'Når foretaket er MVA-registrert, viser Plumme terminer, frister og beregnet MVA her.')} compact />;
  return <div><Section label={t('vat.needsAction')} items={grouped.needsAction} /><Section label={t('vat.upcoming')} items={grouped.upcoming} /><Section label={t('vat.filing.importedArchive', 'Importert arkiv')} items={grouped.importedArchive} /><Section label={t('vat.earlier')} items={grouped.earlier} /></div>;
}

function VatOverview({ terms, totals, onOpen }) {
  const betalt = totals.paid || 0;
  const abetale = totals.toPay || 0;
  const tilgode = totals.receivable || 0;
  const netOpen = abetale - tilgode;
  const statusLabel = netOpen > 0.005 ? t('vat.youOwe', 'Du skylder') : netOpen < -0.005 ? t('vat.youAreOwed', 'Du har til gode') : t('vat.noVatToSettle', 'Ingen MVA å gjøre opp');
  const statusTone = netOpen > 0.005 ? 'amber' : netOpen < -0.005 ? 'plum' : 'green';
  const Box = ({ label, value, tone, help }) => <div className={'mva-sumbox mva-sum-' + tone} {...(help ? { 'data-help-title': label, 'data-help': help } : {})}><div className="mva-sumbox-l">{label}</div><div className="mva-sumbox-v">{value}</div></div>;
  return <div>
    <div className={'mva-sumbox mva-sum-' + statusTone} style={{ marginBottom: 14 }}><div className="mva-sumbox-l">{statusLabel}</div><div className="mva-sumbox-v">{fmt(Math.abs(netOpen))}</div></div>
    <div className="grid-3 gap-14">
      <Box label={t('vat.toPay')} value={fmt(abetale)} tone="amber" help={t('vat.toPayHelp')} />
      <Box label={t('vat.paidIn')} value={fmt(betalt)} tone="green" help={t('vat.paidInHelp')} />
      <Box label={t('vat.refunded')} value={fmt(totals.refunded || 0)} tone="plum" help={t('vat.refundedHelp')} />
    </div>
    <div className="mva-section-label" style={{ marginTop: 26 }}>{t('vat.perTerm')}</div>
    <div className="col gap-10">{terms.map((term) => { const pm = vatOverviewTermMeta(term);const st = TERM_STATUS[term.status]; return <button key={term.key} className="mva-overrow" onClick={() => onOpen(term)}>
      <TerminBadge n={pm.n} /><div className="grow" style={{ minWidth: 0 }}><div className="t-h3" style={{ fontSize: 15 }}>{t('vat.termLabel', '{n}. termin {year}', { n: pm.n, year: pm.year })}</div><div className="t-sm muted" style={{ fontWeight: 600 }}>{pm.period}</div></div>
      <div className="mva-overrow-right">
        <div style={{ textAlign: 'right' }}><div className="t-xs muted mva-overrow-lbl" style={{ fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase' }}>{pm.label}</div><div className="t-h3 t-num" style={{ fontSize: 15 }}>{pm.value}</div></div>
        <span className={'pp ' + st.cls}>{st.label}</span>
      </div>
      <Icon name="chevRight" size={18} className="muted" />
    </button>; })}</div>
  </div>;
}

function EquationBox({ label, value, sub, op, plum, help }) {
  return <React.Fragment>
    <div className={'mva-eqbox' + (plum ? ' plum' : '')} {...(help ? { 'data-help-title': label, 'data-help': help } : {})}>
      <div className="mva-eqbox-l">{label}</div>
      <div className="mva-eqbox-v">{value}</div>
      {sub && <div className="mva-eqbox-sub">{sub}</div>}
    </div>
    {op && <div className="mva-eqop">{op}</div>}
  </React.Fragment>;
}

const vatPaymentRecordLabel = (record, fallbackKind = 'payment') => {
  const kind = record?.settlementKind || fallbackKind;
  if (kind === 'refund') return t('vat.refund', 'Tilbakebetaling');
  if (kind === 'payment') return t('vat.paymentNoun', 'Betaling');
  return t('vat.settlementNoun', 'Oppgjør');
};
const vatPaymentRecordDate = (record) => {
  const value = String(record?.date || record?.createdAt || '').slice(0, 10);
  return value || '-';
};
const vatPaymentDateLabel = (record) => {
  const value = String(record?.date || record?.createdAt || '').slice(0, 10);
  if (!value) return '-';
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString(PlummeI18n.intlLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const vatPaymentAccountOptions = (accounts = []) => {
  const base = (accounts || [])
    .filter((account) => !/^privat\b/i.test(String(account.label || '')))
    .map((account) => ({ value: String(account.value || account.code || ''), label: account.label || account.value || account.code }))
    .filter((account) => account.value);
  return [
    ...base,
    { value: '1920', label: `1920 ${t('vat.acctBankDeposit', 'Bankinnskudd')}` },
    { value: '1950', label: `1950 ${t('vat.acctTaxDeductionAccount', 'Skattetrekkskonto')}` },
    { value: '1900', label: `1900 ${t('vat.acctCash', 'Kontanter')}` },
  ].filter((account, index, list) => list.findIndex((entry) => entry.value === account.value) === index);
};
const vatPaymentAccountLabel = (record, accounts = []) => {
  const code = String(record?.settlementAccountCode || '1920');
  const match = vatPaymentAccountOptions(accounts).find((account) => account.value === code);
  return match?.label || t('vat.accountFallback', '{code} Konto', { code });
};
const vatPaymentReference = (record) => record?.note || record?.reference || '';
// term.dueDate er en ISO-streng laget fra en lokal Date. Leses den med
// toISOString().slice(0,10) havner norsk sommertid en dag for tidlig, sa
// datofeltet fylles fra de lokale komponentene i stedet.
const vatTermDueDateValue = (term) => {
  const raw = term?.dueDate;
  if (!raw) return '';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (value) => String(value).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
};
const vatSurchargeTotal = (record) => Math.max(0, parseAmount(record?.lateInterestAmount)) + Math.max(0, parseAmount(record?.penaltyFeeAmount));
function VatDueDateField({ term, value, onChange }) {
  const dueValue = vatTermDueDateValue(term);
  return <div className="field">
    <div className="mva-fieldhead">
      <label>{t('vat.settlementDate')}</label>
      {dueValue && <button type="button" className="mva-quickchip" title={t('vat.useDueDateHelp')}
        disabled={value === dueValue} onClick={() => onChange(dueValue)} data-help={t('helpTopics.dueDate.body')} data-help-title={t('helpTopics.dueDate.title')}>
        <Icon name="calendar" size={14} />{t('vat.useDueDate')}{term?.frist ? ` · ${term.frist}` : ''}
      </button>}
    </div>
    <Input type="date" value={value} onChange={(event) => onChange(event.target.value)} />
  </div>;
}
function VatSurchargeFields({ open, interest, fee, total, onOpen, onClear, onInterest, onFee }) {
  if (!open) return <button type="button" className="mva-quickchip mva-quickchip-block" onClick={onOpen}>
    <Icon name="plus" size={15} />{t('vat.addSurcharges')}
  </button>;
  return <div className="mva-paybox">
    <div className="mva-fieldhead">
      <div className="t-sm" style={{ fontWeight: 800 }}>{t('vat.surcharges')}</div>
      <button type="button" className="mva-quickchip" onClick={onClear}>{t('vat.removeSurcharges')}</button>
    </div>
    <Field label={t('vat.lateInterest')} hint={t('vat.lateInterestHelp')} className="mt-12" help={t('helpTopics.fee.body')} helpTitle={t('helpTopics.fee.title')}>
      <Currency value={interest} onChange={(event) => onInterest(event.target.value)} placeholder="0" />
    </Field>
    <Field label={t('vat.penaltyFee')} hint={t('vat.penaltyFeeHelp')} className="mt-12" help={t('helpTopics.fee.body')} helpTitle={t('helpTopics.fee.title')}>
      <Currency value={fee} onChange={(event) => onFee(event.target.value)} placeholder="0" />
    </Field>
    <div className="mva-minibox mt-12"><span>{t('vat.surchargeTotal')}</span><b>{fmt(total)}</b></div>
    <div className="t-xs muted mt-12" style={{ fontWeight: 600 }}>{t('vat.surchargeNote')}</div>
  </div>;
}
function VatPaymentAccountSelect({ value, onChange, accounts, direction = 'outbound' }) {
  if (window.InvoicePayAccountSelect) return <window.InvoicePayAccountSelect value={value} onChange={onChange} direction={direction} />;
  const options = vatPaymentAccountOptions(accounts);
  return <Select value={value || options[0]?.value || '1920'} onChange={(event) => onChange(event.target.value)}>
    {options.map((account) => <option key={account.value} value={account.value}>{account.label}</option>)}
  </Select>;
}
const createVatPaymentDraft = (term, accounts = []) => {
  const expectedAmount = Math.max(0, term?.skyldig || term?.tilgode || 0);
  const remainingAmount = Math.max(0, expectedAmount - (term?.betalt || 0));
  const settlementKind = term?.settlementKind || ((term?.skyldig || 0) > 0 ? 'payment' : 'refund');
  const firstAccount = vatPaymentAccountOptions(accounts)[0]?.value || '1920';
  return {
    id: `mva-payment-${Date.now()}`,
    amount: remainingAmount || expectedAmount || 0,
    date: new Date().toISOString().slice(0, 10),
    source: 'manual',
    settlementKind,
    expectedSettlementKind: term?.settlementKind || settlementKind,
    expectedAmount,
    settlementAccountCode: firstAccount,
    note: '',
  };
};

function VatStatusModal({ term, onClose }) {
  const submitted = term.status === 'levert' || term.status === 'levert-betalt';
  const settled = term.status === 'levert-betalt';
  const expected = Math.abs(term.netVat || 0);
  return <Modal open onClose={onClose} title={t('vat.returnStatus')} max={560}
    footer={<Button onClick={onClose}>{t('common.close')}</Button>}>
    <div className="col gap-12">
      <div className={'mva-sumbox mva-sum-' + (submitted ? 'green' : 'amber')}>
        <div className="mva-sumbox-l">{submitted ? t('vat.filing.statusFiled') : t('vat.filing.statusNotFiled')}</div>
        <div className="mva-sumbox-v">{t('vat.termLabel', '{n}. termin {year}', { n: term.n, year: term.year })}</div>
      </div>
      <div className="grid-2 gap-12">
        <div className="mva-minibox"><span>{t('vat.period')}</span><b>{term.period}</b></div>
        <div className="mva-minibox"><span>{t('sharedText.vat.due')}</span><b>{term.fristLong || term.frist}</b></div>
        <div className="mva-minibox"><span>{t('vat.calculated')}</span><b>{fmt(expected)}</b></div>
        <div className="mva-minibox"><span>{t('vat.settlement')}</span><b>{settled ? t('vat.settlementFullyRegistered', 'Fullt registrert') : term.betalt > 0 ? t('vat.settlementPartiallyRegistered', 'Delvis registrert') : t('vat.settlementNotRegistered', 'Ikke registrert')}</b></div>
      </div>
      <window.VatFiledOutsideDetails term={term} />
    </div>
  </Modal>;
}

function VatPaymentEditModal({ term, record, accounts, canDelete, onClose, onSave, onDelete }) {
  const [form, setForm] = useState(record || createVatPaymentDraft(term, accounts));
  const [surchargesOpen, setSurchargesOpen] = useState(vatSurchargeTotal(record) > 0);
  const amountValue = parseAmount(form.amount);
  const isRefund = form.settlementKind === 'refund';
  const interestValue = isRefund ? 0 : Math.max(0, parseAmount(form.lateInterestAmount));
  const feeValue = isRefund ? 0 : Math.max(0, parseAmount(form.penaltyFeeAmount));
  const accountOptions = vatPaymentAccountOptions(accounts);
  const title = form.settlementKind === 'refund' ? t('vat.editRefund', 'Rediger tilbakebetaling') : t('vat.editPayment', 'Rediger betaling');
  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  return <Modal open onClose={onClose} title={title} max={760}
    footer={<React.Fragment>
      {canDelete && <Button variant="danger" icon="trash" style={{ marginRight: 'auto' }} onClick={() => onDelete(record)}>{t('vat.cancelPayment')}</Button>}
      <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="check" disabled={amountValue <= 0} onClick={() => onSave({ ...form, amount: amountValue, lateInterestAmount: interestValue, penaltyFeeAmount: feeValue, note: String(form.note || '').trim() })}>{t('sale.save')}</Button>
    </React.Fragment>}>
    <div className="col gap-14">
      <VatDueDateField term={term} value={String(form.date || '').slice(0, 10)} onChange={(value) => setField('date', value)} />
      <Field label={t('vat.amount')}><Currency value={String(form.amount ?? '')} onChange={(event) => setField('amount', event.target.value)} /></Field>
      {!isRefund && <VatSurchargeFields open={surchargesOpen} interest={form.lateInterestAmount ? String(form.lateInterestAmount) : ''} fee={form.penaltyFeeAmount ? String(form.penaltyFeeAmount) : ''}
        total={amountValue + interestValue + feeValue}
        onOpen={() => setSurchargesOpen(true)}
        onClear={() => { setSurchargesOpen(false); setForm((current) => ({ ...current, lateInterestAmount: 0, penaltyFeeAmount: 0 })); }}
        onInterest={(value) => setField('lateInterestAmount', value)} onFee={(value) => setField('penaltyFeeAmount', value)} />}
      <Field label={form.settlementKind === 'refund' ? t('vat.receivedOn', 'Mottatt på') : t('vat.paidFrom', 'Betalt fra')}><VatPaymentAccountSelect value={form.settlementAccountCode || accountOptions[0]?.value || '1920'} onChange={(value) => setField('settlementAccountCode', value)} accounts={accounts} direction={form.settlementKind === 'refund' ? 'inbound' : 'outbound'} /></Field>
      <Field label={t('vat.reference')} help={t('helpTopics.paymentNote.body')} helpTitle={t('helpTopics.paymentNote.title')}><Input value={form.note || ''} onChange={(event) => setField('note', event.target.value)} placeholder={t('vat.referencePh')} /></Field>
    </div>
  </Modal>;
}

function VatPaymentsModal({ term, accounts, onClose, onAdd, onSave, onDelete }) {
  const records = term.payments || [];
  const [editRecord, setEditRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const handleAdd = () => setEditRecord(createVatPaymentDraft(term, accounts));
  const handleSave = (record) => {
    if (onSave(record) !== false) setEditRecord(null);
  };
  const handleDelete = (record) => {
    if (onDelete(record) !== false) {
      setEditRecord(null);
      setDeleteRecord(null);
    }
  };
  return <React.Fragment><Modal open onClose={onClose} title={t('vat.payments')} max={1040}
    footer={<Button onClick={onClose}>{t('common.close')}</Button>}>
    <p className="t-sm muted" style={{ marginBottom: 16, fontWeight: 600 }}>{t('vat.paymentsHelp')}</p>
    {records.length === 0 ? <EmptyState acc="none" mood="happy" title={t('vat.noPayments')} text={t('vat.noPaymentsHelp', 'Når du registrerer betaling eller tilbakebetaling for terminen, vises den her.')} compact /> : <div className="tbl-wrap">
      <table className="tbl mva-payments-table">
        <thead><tr><th>{t('vat.date')}</th><th>{t('vat.fromAccount')}</th><th className="num">{t('vat.amount')}</th><th data-help={t('helpTopics.paymentNote.body')} data-help-title={t('helpTopics.paymentNote.title')}>{t('vat.reference')}</th><th></th></tr></thead>
        <tbody>{records.map((record) => <tr key={record.id || `${record.date}-${record.amount}`}>
          <td>{vatPaymentDateLabel(record)}</td>
          <td style={{ color: 'var(--plum-700)', fontWeight: 700 }}>{vatPaymentAccountLabel(record, accounts)}</td>
          <td className="num">{fmt(Math.abs(record.amount || 0))}</td>
          <td className="muted" style={{ fontWeight: 600 }}>{vatPaymentReference(record) || '-'}
            {vatSurchargeTotal(record) > 0 && <div className="t-xs">{t('vat.surcharges')}: {fmt(vatSurchargeTotal(record))}</div>}</td>
          <td className="mva-payments-actions"><Button size="sm" variant="ghost" icon="pencil" onClick={() => setEditRecord({ ...record })}>{t('vat.edit')}</Button><Button size="sm" variant="ghost" icon="x" style={{ color: 'var(--error)' }} onClick={() => setDeleteRecord(record)}>{t('vat.cancel')}</Button></td>
        </tr>)}</tbody>
      </table>
    </div>}
    <Button size="sm" variant="secondary" icon="plus" style={{ marginTop: 16 }} onClick={handleAdd}>
      {term.settlementKind === 'refund' ? t('vat.addRefund', 'Legg til tilbakebetaling') : t('vat.addPayment', 'Legg til betaling')}
    </Button>
  </Modal>
  {editRecord && <VatPaymentEditModal term={term} record={editRecord} canDelete={records.some((entry) => entry.id === editRecord.id)} accounts={accounts} onClose={() => setEditRecord(null)} onSave={handleSave} onDelete={setDeleteRecord} />}
  <Modal open={!!deleteRecord} onClose={() => setDeleteRecord(null)} title={t('vat.cancelPaymentTitle')} max={440}
    footer={<React.Fragment><Button variant="ghost" onClick={() => setDeleteRecord(null)}>{t('common.cancel')}</Button><Button variant="danger-solid" icon="trash" onClick={() => handleDelete(deleteRecord)}>{t('vat.cancelPaymentConfirm')}</Button></React.Fragment>}>
    {deleteRecord && <div className="row gap-14" style={{ paddingBottom: 8 }}>

      <p className="t-body">{t('vat.cancelPaymentConfirmPrefix', 'Vil du annullere {kind} på', { kind: vatPaymentRecordLabel(deleteRecord).toLowerCase() })} <b>{fmt(Math.abs(deleteRecord.amount || 0))}</b>{vatPaymentDateLabel(deleteRecord) !== '-' ? t('vat.cancelPaymentConfirmDate', ' registrert {date}', { date: vatPaymentDateLabel(deleteRecord) }) : ''}{t('vat.cancelPaymentConfirmSuffix', '? Beløpet fjernes fra MVA-terminen og kan ikke hentes tilbake.')}</p>
    </div>}
  </Modal>
  </React.Fragment>;
}

// Etter innsending skylder foretaket penger til Skatteetaten. Kontonummeret og
// KID-en hoerer til den innsendte meldingen og kommer fra Skatteetaten — Plumme
// skal ikke gjette dem. Feltene staar derfor tomme med en aerlig begrunnelse
// til koblingen mot Altinn og bank er paa plass, mens beloepet er vaart eget
// tall fra regnskapet og vises som det er.
function VatPaymentHandoff({ term, amount, onPay }) {
  const account = term?.paymentAccountNumber || term?.authorityAccountNumber || '';
  const kid = term?.paymentKid || term?.kid || '';
  const pending = t('vat.awaitingAuthority', 'Kommer fra Skatteetaten');
  return <div className="mva-paybox mt-24">
    <div className="t-h3" style={{ fontSize: 15 }}>{t('vat.payTitle', 'Betal MVA til Skatteetaten')}</div>
    <p className="t-sm muted" style={{ fontWeight: 600, margin: '4px 0 14px' }}>{t('vat.payHelp', 'Kontonummer og KID kommer fra Skatteetaten når meldingen er behandlet. Betaling rett fra banken er ikke koblet på ennå, så betalingen registreres i Plumme inntil videre.')}</p>
    <div className="grid-3 gap-12">
      <div className="mva-minibox"><span>{t('vat.authorityAccount')}</span><b>{account || pending}</b></div>
      <div className="mva-minibox"><span data-help={t('helpTopics.paymentReference.body')} data-help-title={t('helpTopics.paymentReference.title')}>{t('vat.kid')}</span><b>{kid || pending}</b></div>
      <div className="mva-minibox"><span>{t('vat.amount')}</span><b>{fmt(amount)}</b></div>
    </div>
    <div className="row" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
      <Button icon="wallet" onClick={onPay}>{t('vat.pay', 'Betal')}</Button>
    </div>
  </div>;
}

function VatTermDetail({ term, paymentAccounts, onBack, onTolldekl, onBetaling, onSavePayment, onDeletePayment, onSubmit, onOpenBasis, decls, submitMode = 'stepwise', workflowState, onWorkflowStateChange }) {
  const [openCode, setOpenCode] = useState(null);
  const [statusDlgOpen, setStatusDlgOpen] = useState(false);
  const [paymentsDlgOpen, setPaymentsDlgOpen] = useState(false);
  const initialWorkflowState = vatInitialWorkflowState(term);
  const initialSubmitted = initialWorkflowState === 'levert';
  const [localMvaState, setLocalMvaState] = useState(initialWorkflowState); const mvaState = workflowState || localMvaState; const setMvaState = (nextState) => { setLocalMvaState(nextState); onWorkflowStateChange?.(nextState); };
  const [sendDlgOpen, setSendDlgOpen] = useState(false);
  const archiveOnly = term.importedArchiveOnly;
  const statusInfo = archiveOnly
    ? TERM_STATUS.arkiv
    : mvaState === 'levert'
    ? TERM_STATUS[term.status]
    : TERM_STATUS['ikke-levert'];
  const codes = term.codes || [];
  const ug = term.utgaaende || 0,inn = term.inngaaende || 0,tilgode = term.tilgode || 0,skyldig = term.skyldig || 0;
  const settlementTarget = Math.max(0, skyldig || tilgode);
  const netSettledAmount = term.betalt || 0;
  const settledAmount = Math.max(0, netSettledAmount);
  const remainingAmount = Math.max(0, settlementTarget - netSettledAmount);
  const overpaidAmount = Math.max(0, netSettledAmount - settlementTarget);
  const settledInFull = settlementTarget <= 0.005 || netSettledAmount + 0.005 >= settlementTarget;
  const remainingTone = overpaidAmount > 0.005 ? 'red' : remainingAmount > 0.005 ? 'amber' : 'green';
  const settledTone = settledInFull && settlementTarget > 0.005 ? 'green' : settledAmount > 0.005 ? 'amber' : settlementTarget > 0.005 ? 'red' : 'green';
  const remainingLabel = overpaidAmount > 0.005
    ? (skyldig > 0 ? t('vat.overpaid', 'Betalt for mye') : t('vat.overrefunded', 'Fått tilbake for mye'))
    : (skyldig > 0 ? t('vat.remainingToPay', 'Gjenstår å betale') : t('vat.remainingToReceive', 'Gjenstår å få tilbake'));
  const remainingValue = overpaidAmount > 0.005 ? overpaidAmount : remainingAmount;
  const settlementButtonLabel = settlementTarget <= 0.005 ? t('vat.registerSettlement', 'Registrer oppgjør') : skyldig > 0 ? t('vat.registerPayment', 'Registrer betaling') : t('vat.registerRefund', 'Registrer tilbakebetaling');
  const submitted = mvaState === 'levert';
  const direct = submitMode === 'direct';
  const mainActionLabel = direct ? vatDirectActionLabel(mvaState) : vatMainActionLabel(mvaState);
  // Nothing is created or sent in Plumme: the first step only confirms the figures were checked.
  const handleMainAction = () => {
    if (!direct && mvaState === 'opprett') { setMvaState('send'); toast(t('vat.filing.reviewedToast'), 'success'); return; }
    setSendDlgOpen(true);
  };
  // Marks the term filed outside Plumme, or clears that status (audited on the server).
  const handleFiling = async (filing) => {
    const nextFiled = !submitted;
    if (!onSubmit || !(await onSubmit(term, nextFiled, filing))) return false;
    setMvaState(nextFiled ? 'levert' : 'opprett');
    setSendDlgOpen(false);
    toast(nextFiled ? t('vat.filing.markedToast') : t('vat.filing.clearedToast'), nextFiled ? 'success' : 'info');
    return true;
  };
  return <React.Fragment><div>
    <button className="mva-back" onClick={onBack}><Icon name="chevLeft" size={18} />{t('vat.backToTerms')}</button>
    <Card style={{ marginTop: 14 }}>
      <div className="row gap-10" style={{ marginBottom: 18 }}><span className={'pp ' + statusInfo.cls}>{statusInfo.label}</span><span className="pp pp-muted">{t('sharedText.vat.due')} {term.fristLong || term.frist}</span></div>
      {archiveOnly && <div className="mva-sumbox mva-sum-plum" style={{ marginBottom: 18 }}><div className="mva-sumbox-l">{t('vat.importedBasisTitle')}</div><div className="t-sm" style={{ fontWeight: 700, opacity: .85 }}>{t('vat.importedBasisHelp')}</div></div>}
      <div className="mva-eq">
        <EquationBox label={t('vat.outputVat')} value={fmt(ug)} op="−" help={t('vat.outputVatHelp')} />
        <EquationBox label={t('vat.inputVat')} value={fmt(inn)} op="=" help={t('vat.inputVatHelp')} />
        <EquationBox label={skyldig > 0 ? t('vat.vatToPay', 'MVA å betale') : t('vat.vatReceivable', 'MVA til gode')} value={fmt(skyldig > 0 ? skyldig : tilgode)} sub={skyldig > 0 ? t('vat.toTaxAuthority', 'til Skatteetaten') : t('vat.inYourFavor', 'i din favør')} plum help={t('vat.netHelp')} />
      </div>
      <div className="grid-2 gap-14" style={{ marginTop: 26 }}>
        <div className={'mva-sumbox mva-sum-' + remainingTone}><div className="mva-sumbox-l">{remainingLabel}</div><div className="mva-sumbox-v">{fmt(remainingValue)}</div></div>
        <div className={'mva-sumbox mva-sum-' + settledTone}><div className="mva-sumbox-l">{skyldig > 0 ? t('voucherList.paid', 'Betalt') : t('vat.receivedBack', 'Fått tilbake')}</div><div className="mva-sumbox-v">{fmt(settledAmount)}</div></div>
      </div>
      {/* Innsendingen er ikke ferdig for brukeren for pengene er betalt. Neste
          steg staar derfor her, med beloepet som faktisk gjenstaar. */}
      {submitted && !archiveOnly && skyldig > 0 && remainingAmount > 0.005
        && <VatPaymentHandoff term={term} amount={remainingAmount} onPay={onBetaling} />}
      <div className="row gap-10 mva-detail-actions" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
        {submitted && <Button variant="secondary" icon="refresh" className="mva-check-status" onClick={() => setStatusDlgOpen(true)} data-help-title={t('vat.checkStatus')} data-help={t('vat.checkStatusHelp')}>{t('vat.checkStatus')}</Button>}
        {submitted && term.payments?.length > 0 && <Button variant="secondary" icon="eye" className="mva-see-payment" onClick={() => setPaymentsDlgOpen(true)} data-help-title={t('vat.seePayment')} data-help={t('vat.seePaymentHelp')}>{t('vat.seePayment')}</Button>}
        <Button variant="secondary" icon="plus" disabled={archiveOnly || !submitted || settlementTarget <= 0.005} title={archiveOnly ? t('vat.archiveCannotSettle', 'Importert historikk kan ikke gjøres opp som en ny Plumme-termin.') : !submitted ? t('vat.filing.payAfterFiled') : settlementTarget <= 0.005 ? t('vat.noVatToSettleForTerm', 'Terminen har ingen MVA å gjøre opp.') : undefined} onClick={onBetaling}>{settlementButtonLabel}</Button>
      </div>

      <h3 className="t-h3 mt-24" style={{ marginBottom: 4 }} data-help-title={t('vat.codeOverview')} data-help={t('vat.codeOverviewHelp')}>{t('vat.codeOverview')}</h3>
      <p className="t-sm muted" style={{ marginBottom: 12 }}>{t('vat.codeTapHint')}</p>
      <div className="tbl-wrap"><table className="tbl mva-code-tbl">
        <thead><tr><th style={{ width: 70 }}>{t('vat.code')}</th><th>{t('vat.description')}</th><th className="mva-grl-col" style={{ textAlign: 'right' }}>{t('vat.basis')}</th><th className="mva-sats-col" style={{ textAlign: 'right' }}>{t('vat.rate')}</th><th style={{ textAlign: 'right' }}>{t('vat.vat')}</th></tr></thead>
        <tbody>{codes.map((c) => <React.Fragment key={c.code}>
          <tr data-app-control="true" style={{ cursor: 'pointer' }} onClick={() => setOpenCode(openCode === c.code ? null : c.code)}>
            <td><span className="badge badge-neutral" style={{ justifyContent: 'center' }}>{c.code}</span></td>
            <td style={{ fontWeight: 700 }}>{c.desc}</td>
            <td className="num mva-grl-col">
              <button className="link-reset t-num" style={{ textDecoration: 'underline', fontWeight: 800 }} onClick={(e) => { e.stopPropagation(); onOpenBasis && onOpenBasis(c, term); }}>{fmt(c.grunnlag)}</button>
            </td>
            <td className="num mva-sats-col">{c.kind==='vatOnly'?t('vat.amountOnly'):`${c.sats} %`}</td>
            <td className="num" style={{ color: c.mva < 0 ? 'var(--success)' : 'var(--ink)' }}><VatCodeValue row={c} /><span className="mva-sats-inline">{c.kind==='vatOnly'?t('vat.amountOnly'):`${c.sats} %`}</span><span className="mva-grl-inline">{t('vat.basis')} <button className="link-reset" style={{ textDecoration: 'underline', font: 'inherit', color: 'inherit' }} onClick={(e) => { e.stopPropagation(); onOpenBasis && onOpenBasis(c, term); }}>{fmt(c.grunnlag)}</button></span></td>
          </tr>
          {openCode === c.code && <tr><td colSpan="5" style={{ background: 'var(--panel)', padding: '4px 14px 16px' }}>
            <div className="t-h3" style={{ fontSize: 15, margin: '8px 0 2px' }}>{c.code}. {c.desc}</div>
            <div className="t-sm muted" style={{ fontWeight: 600, marginBottom: 12 }}>{c.hint}</div>
            <div className="grid-2 gap-12">
              <div className="mva-minibox"><span>{t('vat.basis')}</span><button className="link-reset t-num" style={{ textDecoration: 'underline', fontWeight: 800 }} onClick={() => onOpenBasis && onOpenBasis(c, term)}>{fmt(c.grunnlag)}</button></div>
              <div className="mva-minibox"><span>{t('vat.rate')}</span><b>{c.kind==='vatOnly'?t('vat.amountOnly'):`${c.sats} %`}</b></div>
              <VatCodeDetails row={c} />
              <div className="mva-minibox"><span>{t('vat.calculatedFrom')}</span><b>{c.kind}</b></div>
            </div>
          </td></tr>}
        </React.Fragment>)}</tbody>
      </table></div>

      <window.VatCustomsDeclarations declarations={decls} onAction={onTolldekl} />

      <div className="grid-2 mt-20" style={{ gap: 14 }}>
        <Button className="btn-lg" icon="upload" onClick={() => onTolldekl('open')} data-help-title={t('vat.registerCustoms')} data-help={t('vat.customsHelp')}>{t('vat.registerCustoms')}</Button>
        <Button className="btn-lg" icon={mvaState === 'levert' ? 'x' : 'check'} variant={mvaState === 'levert' ? 'secondary' : 'primary'} data-help-title={mainActionLabel} data-help={t('vat.flowHelp')} onClick={handleMainAction}>{mainActionLabel}</Button>
      </div>
    </Card>
  </div>
  {statusDlgOpen && <VatStatusModal term={term} onClose={() => setStatusDlgOpen(false)} />}
  {paymentsDlgOpen && <VatPaymentsModal term={term} accounts={paymentAccounts} onClose={() => setPaymentsDlgOpen(false)} onAdd={onBetaling} onSave={onSavePayment} onDelete={onDeletePayment} />}
  {sendDlgOpen && <window.VatFiledOutsideModal term={term} submitted={submitted} onClose={() => setSendDlgOpen(false)} onConfirm={handleFiling} />}
  </React.Fragment>;
}

function MvaBetalingModal({ term, paymentAccounts, onClose, onSave }) {
  const expectedAmount = Math.max(0, term?.skyldig || term?.tilgode || 0);
  const remainingAmount = Math.max(0, expectedAmount - (term?.betalt || 0));
  const expectedType = (term?.skyldig || 0) > 0 ? 'betalt' : 'tilbake';
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [type, setType] = useState(expectedType);
  const [amount, setAmount] = useState(String(remainingAmount || expectedAmount || ''));
  const [kid, setKid] = useState('');const [konto, setKonto] = useState('');
  const [surchargesOpen, setSurchargesOpen] = useState(false);
  const [interest, setInterest] = useState('');
  const [fee, setFee] = useState('');
  const accountOptions = vatPaymentAccountOptions(paymentAccounts);
  const [mottatt, setMottatt] = useState(accountOptions[0]?.value || '1920');
  const settlementKind = type === 'tilbake' ? 'refund' : 'payment';
  const expectedSettlementKind = expectedType === 'tilbake' ? 'refund' : 'payment';
  const amountValue = parseAmount(amount);
  // En tilbakebetaling baerer ingen forsinkelsesrente eller tvangsmulkt. Feltene
  // skjules og nullstilles slik at klienten ikke sender belop serveren uansett
  // ville forkastet.
  const interestValue = settlementKind === 'refund' ? 0 : Math.max(0, parseAmount(interest));
  const feeValue = settlementKind === 'refund' ? 0 : Math.max(0, parseAmount(fee));
  const clearSurcharges = () => { setSurchargesOpen(false); setInterest(''); setFee(''); };
  const title = expectedType === 'tilbake' ? t('vat.registerVatRefund', 'Registrer MVA-tilbakebetaling') : t('vat.registerVatPayment', 'Registrer MVA-betaling');
  const saveLabel = type === 'tilbake' ? t('vat.saveRefund', 'Lagre tilbakebetaling') : t('vat.savePayment', 'Lagre betaling');
  return <Modal open onClose={onClose} title={title} max={760}
    footer={<React.Fragment><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button><Button icon="check" disabled={amountValue <= 0} onClick={() => onSave({ amount: amountValue, date, source: 'manual', settlementKind, expectedSettlementKind, expectedAmount, settlementAccountCode: mottatt, lateInterestAmount: interestValue, penaltyFeeAmount: feeValue, note: [kid ? `KID ${kid}` : '', konto ? `Konto ${konto}` : ''].filter(Boolean).join(' · ') })}>{saveLabel}</Button></React.Fragment>}>
    <div className="col gap-14" style={{ paddingBottom: 4 }}>
      <VatDueDateField term={term} value={date} onChange={setDate} />
      <div><div className="t-sm muted" style={{ marginBottom: 6, fontWeight: 700 }}>{t('vat.settlementType')}</div>
        <Segmented block value={type} onChange={(value) => { setType(value); if (value === 'tilbake') clearSurcharges(); }} options={[{ value: 'betalt', label: t('voucherList.paid', 'Betalt') }, { value: 'tilbake', label: t('vat.receivedBack', 'Fått tilbake') }]} />
      </div>
      <Field label={t('vat.amount')} hint={remainingAmount > 0 ? t('vat.remainingHint', 'Gjenstår {amount}', { amount: fmt(remainingAmount) }) : ''}><Currency value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
      {settlementKind !== 'refund' && <VatSurchargeFields open={surchargesOpen} interest={interest} fee={fee}
        total={amountValue + interestValue + feeValue}
        onOpen={() => setSurchargesOpen(true)} onClear={clearSurcharges} onInterest={setInterest} onFee={setFee} />}
      <div className="mva-paybox">
        <Field label={t('vat.kid')} help={t('helpTopics.paymentReference.body')} helpTitle={t('helpTopics.paymentReference.title')}><Input value={kid} onChange={(e) => setKid(e.target.value)} placeholder={t('vat.kidHelp')} /></Field>
        <Field label={t('vat.authorityAccount')} className="mt-12"><Input value={konto} onChange={(e) => setKonto(e.target.value)} placeholder={t('vat.authorityAccountHelp')} /></Field>
      </div>
      <Field label={type === 'tilbake' ? t('vat.receivedOn', 'Mottatt på') : t('vat.paidFrom', 'Betalt fra')}><VatPaymentAccountSelect value={mottatt} onChange={setMottatt} accounts={paymentAccounts} direction={type === 'tilbake' ? 'inbound' : 'outbound'} /></Field>
    </div>
  </Modal>;
}
function InnsendingerPage({ setRoute, year: controlledYear }) {
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : null;
  const [view, setView] = useState('terminer');
  const [viewOpen, setViewOpen] = useState(false);
  const [openTerm, setOpenTerm] = useState(null); const [dlg, setDlg] = useState(null); const [termWorkflowStates, setTermWorkflowStates] = useState({});
  const year = Number(controlledYear) || Number(backend?.appState?.activeYear || window.PlummeBackend?.appState?.activeYear) || new Date().getFullYear();
  const model = useMemo(() => readVatFilingModel(year), [backend?.appState, backend?.activeBusinessId, year]);
  const terms = model.terms || [];
  const paymentAccounts = model.paymentAccounts || [];
  const selectedTerm = openTerm ? (terms.find((term) => term.key === openTerm.key) || openTerm) : null;
  const VIEWS = { terminer: t('vat.viewTerms', 'Terminer'), oversikt: t('vat.viewOverview', 'Oversikt') };
  // Mutasjonene er delt med arbeidsflaten AI Regnskapsfoereren aapner over sin
  // egen side. Begge kaller de samme funksjonene, saa innsending, tolldeklarasjon
  // og oppgjor oppfoerer seg likt uansett hvor brukeren staar.
  const actions = useMemo(() => window.createVatTermActions({ onCloseDialog: () => setDlg(null) }), []);
  const onTolldekl = async (action, target) => { if (action === 'open') setDlg('tolldekl');
    else if (action === 'correct') setDlg({ type: 'tolldekl', entry: target });
    else if (action === 'annul') await actions.annulCustoms(target);
  };
  const onSaveCustomsDeclaration = (entry) => actions.saveCustoms(entry);
  const onSubmitTerm = (term, nextSubmitted, filing) => actions.submitTerm(term, nextSubmitted, filing);
  const onSavePayment = (record) => actions.savePayment(selectedTerm, record);
  const onDeletePayment = (record) => actions.deletePayment(selectedTerm, record);
  const onOpenBasis = (code, term) => {
    const codeValue = String(code?.code || '');
    if (!codeValue) return;
    window.__plummeVoucherFilter = {
      filter: { months: Array.isArray(term?.months) ? term.months : [0, 11] },
      vat: {
        code: codeValue,
        desc: code?.desc || '',
        term: term?.n ? t('vat.termLabel', '{n}. termin {year}', { n: term.n, year: term.year }) : String(term?.year || year),
        from: 'innsendinger',
      },
    };
    setRoute && setRoute('allebilag');
  };
  const workflowState = (term) => termWorkflowStates[term?.key] || vatInitialWorkflowState(term); const setTermWorkflowState = (term, nextState) => setTermWorkflowStates((current) => ({ ...current, [term.key]: nextState })); const prepareReturn = (term) => { setTermWorkflowState(term, 'send'); setOpenTerm(term); toast(t('vat.reviewReadyToast', 'MVA-meldingen er klargjort for kontroll'), 'success'); };
  useVatFilingPageControlAdapter({ year, view, setView, selectedTerm, terms, totals: model.totals, blocked: model?.blocked === true || model?.importedArchiveOnly === true, accountingBasis: model?.accountingBasis, vatRegistered: model.vatRegistered !== false, workflowState, prepareReturn, openTerm: setOpenTerm, openDialog: setDlg, openBasis: onOpenBasis });
  if (model?.vatRegistered === false) return null;
  if (model?.blocked) return <Card><EmptyState acc="none" mood="concerned" title={t('vat.incompleteBasisTitle')} text={t('vat.incompleteBasisText')} compact /></Card>;
  return <div>
      {window.ImportedComplianceNotice&&<window.ImportedComplianceNotice year={year} area="vat"/>}
      <div className="row between wrap gap-12" style={{ marginBottom: 22 }}>
      {selectedTerm
        ? <div className="t-h2">{t('vat.termLabel', '{n}. termin {year}', { n: selectedTerm.n, year: selectedTerm.year })}</div>
        : <div className="popwrap">
            <button className="mva-viewdrop" onClick={() => setViewOpen((o) => !o)} data-help-title={t('vat.view')} data-help={t('vat.viewHelp')}>{VIEWS[view]}<Icon name="chevDown" size={18} /></button>
            {viewOpen && <React.Fragment><div className="pop-scrim" onClick={() => setViewOpen(false)} />
              <div className="pop pop-menu" style={{ minWidth: 200 }}>
                {Object.entries(VIEWS).map(([k, l]) => <button key={k} onClick={() => { setView(k); setViewOpen(false); }}>{l}{view === k && <Icon name="check" size={16} style={{ marginLeft: 'auto' }} />}</button>)}
              </div></React.Fragment>}
          </div>}
    </div>
    {selectedTerm
      ? <VatTermDetail key={selectedTerm.key} term={selectedTerm} paymentAccounts={paymentAccounts} decls={selectedTerm.declarations} workflowState={workflowState(selectedTerm)} onWorkflowStateChange={(nextState) => setTermWorkflowState(selectedTerm, nextState)} onBack={() => setOpenTerm(null)} onTolldekl={onTolldekl} onBetaling={() => setDlg('betaling')} onSavePayment={onSavePayment} onDeletePayment={onDeletePayment} onSubmit={onSubmitTerm} onOpenBasis={onOpenBasis} />
      : view === 'terminer'
      ? <VatTermsView terms={terms} onOpen={setOpenTerm} />
      : <VatOverview terms={terms} totals={model.totals || {}} onOpen={setOpenTerm} />}
    {(dlg === 'tolldekl' || dlg?.type === 'tolldekl') && <TolldeklarasjonModal term={selectedTerm} entry={dlg?.entry || null} onClose={() => setDlg(null)} onSave={onSaveCustomsDeclaration} />}
    {dlg === 'betaling' && <MvaBetalingModal term={selectedTerm} paymentAccounts={paymentAccounts} onClose={() => setDlg(null)} onSave={onSavePayment} />}
  </div>;
}

// ---------------- EIENDELER ----------------
const readFixedAssetsModel = (year) => window.PlummeBackend?.getFixedAssetsModel?.({ year }) || { year, groups: [], assets: [], groupRows: [], totals: { active: 0, writtenOff: 0, sold: 0, all: 0, bookValue: 0 } };
const fixedAssetToday = () => new Date().toISOString().slice(0,10);
const assetGroupById = (groups, id) => groups.find((group) => group.id === id) || groups[0] || { id:'d', label:t('asset.groupDFallback'), rate:20, assetAccountCode:'1200' };
const fixedAssetMoney = (value) => window.PlummeMoneyInput.number(value, 0);
const fixedAssetLineNet = (line) => {
  const amount = fixedAssetMoney(line?.amount);
  const vat = Number(line?.vat) || 0;
  return line?.amountMode === 'gross' && vat > 0 && line?.reverse !== '86' ? amount / (1 + vat / 100) : amount;
};
const fixedAssetLineVat = (line) => line?.reverse === '86' ? 0 : fixedAssetLineNet(line) * ((Number(line?.vat) || 0) / 100);
const fixedAssetNewLine = (groups, group, index = 1) => {
  const selected = assetGroupById(groups, group);
  return { id: Date.now() + index, desc: '', amount: '', amountMode: 'net', vat: window.purchaseNewLineVatRate(), acct: selected.assetAccountCode || '1200', reverse: '' };
};
const fixedAssetAttachmentName = (attachment) => attachment?.name || attachment?.file?.name || t('asset.attachmentFallback'); function fixedAssetSupplierOptions(){
  return (window.PlummeBackend?.configured ? window.SUPPLIERS : (typeof SUPPLIERS !== 'undefined' ? SUPPLIERS : [])) || [];
}
function EiendelSupplierPicker({ supplierId, supplierName, onPick }){
  const [query,setQuery]=useState(supplierName||'');
  const [open,setOpen]=useState(false);
  const [suppliers,setSuppliers]=useState(fixedAssetSupplierOptions);
  const [modal,setModal]=useState(null);
  useEffect(()=>{
    const selected=supplierId?fixedAssetSupplierOptions().find((supplier)=>supplier.id===supplierId):null;
    setQuery(selected?.name||supplierName||'');
  },[supplierId,supplierName]);
  useEffect(()=>{
    const refresh=()=>setSuppliers(fixedAssetSupplierOptions());
    refresh();
    window.addEventListener('plumme-suppliers-changed',refresh);
    window.addEventListener('plumme-backend-state-changed',refresh);
    window.addEventListener('plumme-backend-state',refresh);
    return()=>{
      window.removeEventListener('plumme-suppliers-changed',refresh);
      window.removeEventListener('plumme-backend-state-changed',refresh);
      window.removeEventListener('plumme-backend-state',refresh);
    };
  },[]);
  const needle=String(query||'').trim().toLowerCase();
  const orgNumberLookup=Boolean(String(query||'').replace(/\D/g,''))&&/^[\s\d.-]+$/.test(String(query||''));
  const matches=suppliers
    .filter((supplier)=>supplier?.status!=='passiv')
    .filter((supplier)=>{
      if(!needle) return true;
      return [supplier.name,supplier.org,supplier.organizationNumber,supplier.email,supplier.phone].some((part)=>String(part||'').toLowerCase().includes(needle));
    })
    .slice(0,8);
  const exact=needle&&suppliers.some((supplier)=>String(supplier.name||'').trim().toLowerCase()===needle);
  const choose=(supplier)=>{
    setQuery(supplier?.name||'');
    setOpen(false);
    onPick&&onPick(supplier||{id:'',name:''});
  };
  const openAdd=()=>{
    setOpen(false);
    setModal(window.supplierDraftFromQuery(query));
  };

  const onSupplierSaved=(supplier,draft)=>{
    setSuppliers(fixedAssetSupplierOptions());
    setQuery(supplier.name||draft.name.trim());
    onPick&&onPick(supplier);
  };
  return <React.Fragment>
    <div className="combo">
      <Input icon="search" value={query} onFocus={()=>setOpen(true)} onChange={(event)=>{const value=event.target.value;setQuery(value);onPick&&onPick({id:'',name:value});setOpen(true);}} placeholder={t('vat.supplierSearch')}/>
      {open&&<React.Fragment>
        <button type="button" className="combo-scrim" aria-label={t('vat.supplierClose')} onClick={()=>setOpen(false)}/>
        <div className="combo-pop purchase-supplier-pop">
          {matches.map((supplier)=><button key={supplier.id||supplier.name} type="button" className="combo-item" onClick={()=>choose(supplier)}>
            <span><strong>{supplier.name}</strong>{(supplier.org||supplier.organizationNumber)&&<small className="muted" style={{display:'block',marginTop:2}}>{supplier.org||supplier.organizationNumber}</small>}</span>
            <Icon name="check" size={16}/>
          </button>)}
          {window.SupplierRegistryLookup&&window.PlummeBusinessRegistry.lookupEnabled()&&<window.SupplierRegistryLookup query={query} embedded onApply={(match)=>{setOpen(false);setModal(window.supplierDraftWithRegistryMatch(match,modal));}}/>}
          {!matches.length&&!orgNumberLookup&&<div className="combo-empty t-sm muted">{t('vat.supplierNone')}</div>}
          {!exact&&needle&&<button type="button" className="combo-add" onClick={openAdd}><Icon name="plus" size={15}/> {t('buy.addSupplierQuery','Legg til «{query}»',{query:query.trim()})}</button>}
          {!needle&&<button type="button" className="combo-add" onClick={openAdd}><Icon name="plus" size={15}/> {t('sharedText.buy.supplierNew')}</button>}
        </div>
      </React.Fragment>}
    </div>
    <window.SupplierCreateModal draft={modal} onDraftChange={setModal} onClose={()=>setModal(null)} onSaved={onSupplierSaved}
      labels={{title:'vat.supplierNew',name:'vat.supplierName',org:'vat.supplierOrg',email:'vat.supplierEmail',phone:'vat.supplierPhone'}}/>
  </React.Fragment>;
}
if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
  window.PlummeVatPresentationTest = { amountMeta, groupVatTerms, vatOverviewTermMeta, vatInitialWorkflowState, vatMainActionLabel, vatDirectActionLabel, vatTermDueDateValue, vatSurchargeTotal };
}
// MVA-meldingen monteres ogsaa av arbeidsflaten paa AI Regnskapsfoerer-siden.
// Den skal vaere den samme komponenten, ikke en slankere kopi — ellers viser de
// to flatene hvert sitt tallgrunnlag for den samme innsendingen.
Object.assign(window,{InnsendingerPage,readFixedAssetsModel,readVatFilingModel,VatTermDetail,MvaBetalingModal});
